import { notification, Popover } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  GetListingActivityRequest,
  GetListingActivityResponse,
  ListingActivity,
  activityPayout,
  activitySold,
} from '../api/models';
import APIClient from '../api/api';
import moment from 'moment';
import { formatCurrency } from '../utils';
import AddSalesRecordModal from '../components/AddSalesRecordModal';
import { SalesHistoryImportModal } from '../components/SalesHistoryImportModal';
import DateRangeFilter, { useDateRangeFilter } from '../components/DateRangeFilter';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Column,
  DataTable,
  DomainName,
  FilterPill,
  Num,
  Pagination,
  StatusTag,
  TagTone,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const PAGE_SIZE = 25;

const typeFilterOptions: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'sold', label: 'Sale' },
  { value: 'payout-full', label: 'Payout (Full)' },
  { value: 'payout-installment', label: 'Payout (Installment)' },
  { value: 'transferComplete', label: 'Transfer Complete' },
  { value: 'transferRequest', label: 'Transfer Request' },
  { value: 'cancelled', label: 'Cancelled' },
];

const venueFilterOptions: { value: string; label: string }[] = [
  { value: '', label: 'All Venues' },
  { value: 'afternic', label: 'Afternic' },
  { value: 'sedo', label: 'Sedo' },
  { value: 'dan', label: 'Dan' },
  { value: 'atom', label: 'Atom' },
];

const accountOptions = [
  'parity-wholesale',
  'parity1',
  'abbeycatalyst',
  'parity-software',
  'exclusivellc',
];

const typeTone: Record<string, TagTone> = {
  sold: 'success',
  payout: 'accent',
  transferComplete: 'success',
  transferRequest: 'warn',
  cancelled: 'danger',
};

interface SortState {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

const SalesActivity = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<GetListingActivityResponse>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryImport, setShowHistoryImport] = useState(false);

  const { dateRange, setDateRange, selectedQuickRange, setSelectedQuickRange } = useDateRangeFilter('this-month');
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') || '');
  const [venueFilter, setVenueFilter] = useState<string>('');
  const [accountFilter, setAccountFilter] = useState<string>('');
  const domainFilter = searchParams.get('domain') || '';

  const [sortState, setSortState] = useState<SortState>({ sortBy: 'date', sortDirection: 'desc' });
  const sortStateRef = useRef(sortState);
  sortStateRef.current = sortState;

  useEffect(() => {
    loadSalesActivity(1, sortStateRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, typeFilter, venueFilter, accountFilter, domainFilter]);

  function setDomain(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('domain', value);
    else next.delete('domain');
    setSearchParams(next);
  }

  async function loadSalesActivity(pageNum: number, sort: SortState) {
    setLoading(true);
    try {
      const req: GetListingActivityRequest = {
        start: dateRange[0].toDate(),
        end: dateRange[1].toDate(),
      };

      if (typeFilter === 'payout-full') {
        req.type = 'payout';
        req.payoutType = 'full';
      } else if (typeFilter === 'payout-installment') {
        req.type = 'payout';
        req.payoutType = 'installment';
      } else if (typeFilter) {
        req.type = typeFilter as any;
      }

      if (venueFilter) req.source = venueFilter;
      if (accountFilter) req.account = accountFilter;
      if (domainFilter) req.domain = domainFilter;
      if (sort.sortBy) req.sortBy = sort.sortBy;
      if (sort.sortDirection) req.sortDirection = sort.sortDirection;

      const resp = await APIClient.getListingActivity({ page: pageNum, pageSize: PAGE_SIZE }, req);
      setData(resp);
      setPage(resp.currentPage || pageNum);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load sales activity.' });
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(key: string) {
    setSortState((prev) => {
      let next: SortState;
      if (prev.sortBy !== key) next = { sortBy: key, sortDirection: 'desc' };
      else if (prev.sortDirection === 'desc') next = { sortBy: key, sortDirection: 'asc' };
      else next = {};
      loadSalesActivity(1, next);
      return next;
    });
  }

  const typeLabel = typeFilterOptions.find((o) => o.value === typeFilter)?.label || 'All';
  const venueLabel = venueFilterOptions.find((o) => o.value === venueFilter)?.label || 'All';
  const dateLabel = selectedQuickRange
    ? selectedQuickRange.replace(/-/g, ' ')
    : `${dateRange[0].format('MMM D')} – ${dateRange[1].format('MMM D')}`;

  const columns: Column<ListingActivity>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (l) => (
        <span className="inline-flex items-center gap-1.5 group">
          <DomainName name={l.domain} />
          <button
            type="button"
            title={`Filter by ${l.domain}`}
            onClick={() => setDomain(l.domain)}
            className="text-text-3 hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <SearchOutlined style={{ fontSize: 11 }} />
          </button>
        </span>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (l) => <StatusTag tone={typeTone[l.type] ?? 'neutral'}>{l.type}</StatusTag>,
    },
    {
      key: 'venue',
      title: 'Venue',
      render: (l) => (
        <span className="text-text-2 text-[12.5px]">
          {l.source}
          <span className="text-text-3"> · {l.account}</span>
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'When',
      align: 'right',
      sortable: true,
      sortDirection: sortState.sortBy === 'date' ? sortState.sortDirection : undefined,
      onSort: () => toggleSort('date'),
      render: (l) => (
        <span className="font-mono text-[12.5px] text-text-2 tabular-nums" title={moment(l.createdAt).format('LLL')}>
          {moment(l.createdAt).format('MMM D, HH:mm')}
        </span>
      ),
    },
    {
      key: 'amount',
      title: 'Amount',
      align: 'right',
      sortable: true,
      sortDirection: sortState.sortBy === 'amount' ? sortState.sortDirection : undefined,
      onSort: () => toggleSort('amount'),
      render: (l) => {
        const amt = parseAmount(l);
        return <Num value={amt} format="currency" />;
      },
    },
    {
      key: 'details',
      title: 'Details',
      render: (l) => <span className="text-text-3 text-[12.5px]">{getDetails(l)}</span>,
    },
  ];

  const activeFilterCount =
    (typeFilter ? 1 : 0) + (venueFilter ? 1 : 0) + (accountFilter ? 1 : 0) + (domainFilter ? 1 : 0);

  return (
    <>
      <PageHead
        title="Sales Activity"
        subtitle="All sales, payouts, and transfer activity across venues."
        actions={
          <>
            <Button variant="ghost" onClick={() => setShowHistoryImport(true)}>
              Import History
            </Button>
            <Button variant="primary" iconLeft={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
              Add Record
            </Button>
          </>
        }
      />

      <PageToolbar>
        <Popover
          trigger="click"
          placement="bottomLeft"
          getPopupContainer={() => document.body}
          content={
            <div className="p-2 min-w-[280px]">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange as any}
                selectedQuickRange={selectedQuickRange}
                onQuickRangeChange={setSelectedQuickRange}
              />
            </div>
          }
        >
          <span>
            <FilterPill label="Date" value={dateLabel} active={!!selectedQuickRange} />
          </span>
        </Popover>

        <SelectPill
          label="Type"
          value={typeLabel}
          options={typeFilterOptions}
          selected={typeFilter}
          onChange={setTypeFilter}
        />
        <SelectPill
          label="Venue"
          value={venueLabel}
          options={venueFilterOptions}
          selected={venueFilter}
          onChange={setVenueFilter}
        />
        <SelectPill
          label="Account"
          value={accountFilter || 'All'}
          options={[{ value: '', label: 'All' }, ...accountOptions.map((a) => ({ value: a, label: a }))]}
          selected={accountFilter}
          onChange={setAccountFilter}
        />
        <TextPill label="Domain" value={domainFilter} placeholder="example.com" onChange={setDomain} />

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setTypeFilter('');
              setVenueFilter('');
              setAccountFilter('');
              setDomain('');
            }}
            className="text-[11px] text-text-3 hover:text-text font-mono px-1"
          >
            clear ({activeFilterCount})
          </button>
        )}

        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {data ? <>
            total <b className="text-text-2">{data.total.toLocaleString()}</b>
            {data.extra?.totalSold ? <> · sold <b className="text-text-2">{formatCurrency(data.extra.totalSold)}</b></> : null}
            {data.extra?.totalPayouts ? <> · payouts <b className="text-text-2">{formatCurrency(data.extra.totalPayouts)}</b></> : null}
          </> : 'loading…'}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={data?.content ?? []}
        rowKey={(l, i) => l.domain + '-' + l.createdAt + '-' + i}
        loading={loading}
        emptyMessage="No sales activity in this range."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{data?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{data?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data?.total ?? 0}
            onPageChange={(p) => loadSalesActivity(p, sortState)}
            loading={loading}
          />
        }
      />

      <AddSalesRecordModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => loadSalesActivity(page, sortState)}
      />

      {showHistoryImport && (
        <SalesHistoryImportModal onClose={() => setShowHistoryImport(false)} />
      )}
    </>
  );
};

interface SelectPillProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
}

function SelectPill({ label, value, options, selected, onChange }: SelectPillProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      content={
        <div className="p-1 flex flex-col min-w-[160px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`text-left px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                selected === opt.value ? 'text-accent font-semibold' : 'text-text-2'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      }
    >
      <span>
        <FilterPill label={label} value={value} active={!!selected} />
      </span>
    </Popover>
  );
}

interface TextPillProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

function TextPill({ label, value, placeholder, onChange }: TextPillProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    function handleClick(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        dropRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, reposition]);

  return (
    <>
      <span ref={triggerRef} onClick={() => setOpen((o) => !o)}>
        <FilterPill label={label} value={value || 'Any'} active={!!value} />
      </span>
      {open && createPortal(
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 1050 }}
          className="bg-panel border border-border-strong rounded-lg shadow-lg p-2 w-[220px]"
        >
          <input
            type="text"
            value={draft}
            placeholder={placeholder}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange(draft);
                setOpen(false);
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            className="w-full max-w-full box-border px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
          />
          <div className="flex justify-end gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onChange('');
                setOpen(false);
              }}
              className="text-[11px] text-text-3 hover:text-text px-2 py-1"
            >
              clear
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              className="text-[11px] text-accent font-semibold hover:opacity-80 px-2 py-1"
            >
              apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function parseAmount(a: ListingActivity): number | null {
  try {
    if (a.type === 'sold') {
      return activitySold.parse(JSON.parse(a.data)).price;
    }
    if (a.type === 'payout') {
      return activityPayout.parse(JSON.parse(a.data)).amount;
    }
  } catch {}
  return null;
}

function getDetails(a: ListingActivity): React.ReactNode {
  try {
    if (a.type === 'cancelled') return 'Sale was cancelled';
    if (a.type === 'sold') {
      const sold = activitySold.parse(JSON.parse(a.data));
      return `Sold ${formatCurrency(sold.price)} · ${sold.type} · ${sold.payoutType}`;
    }
    if (a.type === 'transferComplete') return 'Transfer complete';
    if (a.type === 'transferRequest') return 'Transfer requested';
    if (a.type === 'payout') {
      const payout = activityPayout.parse(JSON.parse(a.data));
      if (payout.type === 'installment') {
        const parts: React.ReactNode[] = ['LTO'];
        if (payout.installment && payout.totalInstallments) {
          parts.push(' ');
          parts.push(
            <Link key="inst" to={`/sales/activity?domain=${encodeURIComponent(a.domain)}&type=payout-installment`} className="text-accent hover:opacity-80">
              {payout.installment} of {payout.totalInstallments}
            </Link>
          );
        }
        parts.push(` · ${formatCurrency(payout.amount)} paid`);
        if (payout.remainingBalance) {
          parts.push(` · ${formatCurrency(payout.remainingBalance)} remaining`);
        }
        return <>{parts}</>;
      }
      return `${formatCurrency(payout.amount)} paid`;
    }
  } catch {}
  return '';
}

export default SalesActivity;
